import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { contentLang } from "@/lib/locale";
import { insightLinks, INDEX_COPY, type InsightLink } from "@/content/insights-index";
import { SectionShell, SectionBody } from "@/components/SectionShell";
import { SectionHeader } from "@/components/SectionHeader";
import { ExpandingSection } from "@/components/ExpandingSection";

type Props = {
  /** Render the "go to /insights" link (hide it when already on the hub). */
  showHubLink?: boolean;
  className?: string;
  /** Numer sekcji w rytmie strony. */
  index?: string;
};

function GuideCard({ item, featured = false }: { item: InsightLink; featured?: boolean }) {
  return (
    <Link
      to={item.href}
      className={`group relative flex h-full flex-col bg-[#050706] transition-colors hover:bg-white/[0.05] ${
        featured ? "gap-4 p-7 md:p-10" : "gap-2.5 p-5 md:p-6"
      }`}
    >
      <span
        className="absolute left-0 top-0 h-px w-0 bg-lime-300 transition-all duration-500 group-hover:w-full"
        aria-hidden
      />
      <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-lime-300">
        {item.topic}
      </span>
      <span
        className={
          featured
            ? "h-display text-2xl md:text-4xl text-white transition-colors group-hover:text-white"
            : "text-[15px] md:text-[16px] leading-snug text-white transition-colors group-hover:text-white"
        }
      >
        {item.title}
      </span>
      <span
        className={`leading-[1.7] text-white/60 ${featured ? "text-[15px] md:text-base max-w-xl" : "text-[13px]"}`}
      >
        {item.summary}
      </span>
      <span className="mt-auto flex items-center gap-3 pt-5 font-mono text-[10px] uppercase tracking-[0.24em] text-white/35 transition-colors group-hover:text-lime-300">
        <span className="h-px w-6 bg-current transition-all duration-500 group-hover:w-10" aria-hidden />
        <span aria-hidden>→</span>
      </span>
    </Link>
  );
}

/**
 * Insights: jeden wyróżniony przewodnik (najsilniejszy SEO), dwa poboczne,
 * reszta w rozwijanej liście. Wszystkie linki są w DOM - crawler widzi komplet.
 */
export function InsightsLinkIndex({ showHubLink = true, className = "", index = "10" }: Props) {
  const { i18n } = useTranslation();
  const lang = contentLang(i18n.language);
  const links = insightLinks(lang);
  const copy = INDEX_COPY[lang];
  const [open, setOpen] = useState(false);

  const [featured, ...others] = links;
  const secondary = others.slice(0, 3);
  const rest = others.slice(3);

  return (
    <SectionShell tone="panel" size="md" className={className}>
      <SectionHeader
        index={index}
        eyebrow={copy.eyebrow}
        titleLines={[copy.title]}
        titleClassName="text-3xl md:text-5xl"
        lead={
          showHubLink ? (
            <Link
              to="/insights"
              className="inline-block font-mono text-[10px] uppercase tracking-[0.24em] text-lime-300 hover:translate-x-1 transition-transform"
            >
              {copy.all}
            </Link>
          ) : undefined
        }
      />

      <SectionBody>
        <div className="grid gap-px border border-white/10 bg-white/10">
          <GuideCard item={featured} featured />
          <div className="grid gap-px bg-white/10 md:grid-cols-3">
            {secondary.map((item) => (
              <GuideCard key={item.href} item={item} />
            ))}
          </div>
        </div>

        {rest.length > 0 && (
          <>
            <ExpandingSection open={open} className="mt-px">
              <ul className="grid gap-px bg-white/10 border-x border-b border-white/10 md:grid-cols-2">
                {rest.map((item) => (
                  <li key={item.href} className="bg-[#050706]">
                    <GuideCard item={item} />
                  </li>
                ))}
              </ul>
            </ExpandingSection>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="mt-6 inline-flex items-center gap-3 border border-white/15 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.24em] text-white/70 transition-colors hover:border-lime-300 hover:text-lime-300"
            >
              {open ? copy.less : copy.more(rest.length)}
              <span aria-hidden>{open ? "−" : "+"}</span>
            </button>
          </>
        )}
      </SectionBody>
    </SectionShell>
  );
}

export default InsightsLinkIndex;
